import { Lock, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { itemQuantityKey } from '@obraplan/core/domain/takeoff/consolidation';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/feedback/states';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { ComoFunciona } from './ComoFunciona';
import { NumeroOpcionalInput } from './campos';
import { metros } from './formato';
import { useSession } from '@/features/session/use-session';
import { aprovacaoDe, textoDaAprovacao, type ServicosAprovados } from './servicos-aprovados';
import {
  divergentUnits,
  quantitiesOfUnit,
  shadowOf,
  unitsOf,
  type TypologyDraft,
  type UnitDraft,
  type WorkspaceAction,
  type WorkspaceDraft,
} from './typology-draft';

/**
 * Os aptos desta tipologia, e o que cada um faz de diferente (RN-167).
 *
 * O quantitativo mora na **tipologia**: os cento e vinte aptos herdam, e nenhum deles
 * guarda cópia. O que existe aqui é a exceção — quando o cliente do 1204 junta dois
 * quartos, aquele apto ganha uma linha-sombra que vale só ali, e o resto continua
 * seguindo o desenho.
 *
 * Por isso o cartão é pequeno: doze andares que seguem a tipologia são doze marcas numa
 * linha, e não doze caixas dizendo "segue a tipologia". O que diverge é que ganha
 * destaque — e a contagem no cabeçalho diz se há algo a ver antes de alguém procurar.
 *
 * O **motivo é obrigatório** (RN-168), e é a parte que não parece importante até seis
 * meses depois: sem ele, ninguém sabe por que aquele apto pede quarenta tijolos a mais, e
 * a divergência acaba apagada por quem não entendeu.
 */
export function AptosDaTipologia({
  catalog,
  workspace,
  draft,
  dispatch,
  aprovados,
}: {
  catalog: ServiceCatalog;
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  dispatch: React.Dispatch<WorkspaceAction>;
  /**
   * Os serviços aprovados no orçamento (Fase 33, RN-438): a divergência do apto mudaria a
   * quantidade deles, e por isso o item do aprovado só se lê aqui.
   */
  aprovados?: ServicosAprovados;
}) {
  const [aberto, setAberto] = useState<string | undefined>(undefined);

  const meus = unitsOf(workspace).filter((unit) => unit.typology.unitTypeId === draft.unitTypeId);
  const divergentes = divergentUnits(workspace, draft);
  const escolhido = meus.find((unit) => unit.key === aberto);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Aptos de {draft.name}</CardTitle>
        <CardDescription>
          {meus.length === 0
            ? 'Os aptos saem da estrutura: cada pavimento ligado ao desenho desta tipologia produz um.'
            : `${String(meus.length)} ${meus.length === 1 ? 'apto herda' : 'aptos herdam'} o desenho da tipologia${
                divergentes.length === 0
                  ? ', e nenhum diverge.'
                  : `; ${String(divergentes.length)} ${divergentes.length === 1 ? 'diverge' : 'divergem'}.`
              } Abra um andar para lançar a exceção dele.`}
        </CardDescription>
        <ComoFunciona>
          Cada andar herda o desenho e <strong>não guarda cópia dele</strong>. O que entra aqui é só
          a exceção: o apto que fez diferente ganha a quantidade dele naquele item, com o motivo —
          obrigatório, porque é ele que explica a diferença para quem abrir isto seis meses depois
          (RN-167, RN-168).
        </ComoFunciona>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {meus.length === 0 ? (
          <EmptyState
            title="Nenhum apto ainda"
            description="Ligue os pavimentos ao desenho desta tipologia na aba de montagem, em Pavimentos e desenhos."
          />
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Aptos da tipologia">
              {meus.map((unit) => {
                const sombra = shadowOf(workspace, unit.key);
                const quantas = Object.keys(sombra?.lines ?? {}).length;
                const escolhida = unit.key === aberto;
                return (
                  <button
                    key={unit.key}
                    type="button"
                    aria-pressed={escolhida}
                    onClick={() => setAberto(escolhida ? undefined : unit.key)}
                    className={cn(
                      'flex h-11 items-center gap-1.5 rounded-md border px-2.5 text-xs md:h-8',
                      escolhida
                        ? 'border-(--color-brand) bg-(--color-surface-muted) font-medium'
                        : quantas > 0
                          ? 'border-(--color-state-warn) hover:bg-(--color-surface-muted)'
                          : 'border-(--color-border-subtle) text-(--color-content-muted) hover:bg-(--color-surface-muted) hover:text-(--color-content)',
                    )}
                  >
                    {unit.floor.name}
                    {quantas > 0 && (
                      <span className="tabular text-(--color-state-warn)">
                        · {quantas} {quantas === 1 ? 'divergência' : 'divergências'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {escolhido !== undefined && (
              /*
                `key` pelo apto: sem ela o React reaproveita a instância ao trocar de
                apartamento, e o motivo digitado para um fica no campo do outro — a
                justificativa da RN-168 acabaria colada na divergência errada, sem nada
                na tela denunciando.
              */
              <ItensDoApto
                key={escolhido.key}
                catalog={catalog}
                workspace={workspace}
                draft={draft}
                unit={escolhido}
                dispatch={dispatch}
                {...(aprovados === undefined ? {} : { aprovados })}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * O que este apto consome, item a item.
 *
 * Só os itens que a tipologia lançou aparecem: divergir de um item que ela não tem seria
 * acrescentar uma linha ao apto, que é outra coisa (RN-174) e ainda não está na tela.
 */
function ItensDoApto({
  catalog,
  workspace,
  draft,
  unit,
  dispatch,
  aprovados,
}: {
  catalog: ServiceCatalog;
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  unit: UnitDraft;
  dispatch: React.Dispatch<WorkspaceAction>;
  aprovados?: ServicosAprovados;
}) {
  const [motivo, setMotivo] = useState('');
  // Quem está decidindo — o autor que a RN-168 pede ao lado da data e do motivo.
  const sessao = useSession();
  const daTipologia = draft.itemQuantities ?? {};
  const noApto = quantitiesOfUnit(workspace, unit);
  const sombra = shadowOf(workspace, unit.key);
  const semMotivo = motivo.trim() === '';

  const lancados = catalog.disciplines.flatMap((disciplina) =>
    disciplina.services.flatMap((servico) =>
      servico.items
        .filter((item) => daTipologia[itemQuantityKey(servico.key, item.key)] !== undefined)
        .map((item) => ({ servico, item, chave: itemQuantityKey(servico.key, item.key) })),
    ),
  );

  return (
    <div className="flex flex-col gap-3 rounded-md border border-(--color-border-subtle) p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {unit.floor.name} · {draft.name}
        </h3>
        <Input
          aria-label="Motivo da divergência"
          placeholder="Motivo — obrigatório para divergir"
          className="h-9 min-w-64 flex-1 md:max-w-md"
          value={motivo}
          onChange={(event) => setMotivo(event.target.value)}
        />
      </div>

      {lancados.length === 0 ? (
        <p className="text-xs text-(--color-content-muted)">
          A tipologia ainda não lançou nenhum item. Divergir pressupõe algo de que divergir.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-(--color-border-subtle)">
          {lancados.map(({ servico, item, chave }) => {
            const linha = sombra?.lines[chave];
            const daqui = noApto[chave] ?? 0;
            const aprovado = aprovacaoDe(aprovados, servico.key);

            return (
              <li
                key={chave}
                className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{item.description}</span>
                  <span className="block truncate text-(--color-content-muted)">
                    {servico.name} · tipologia {metros(daTipologia[chave] ?? 0, 3)}
                    {/* Autor, data e motivo: os três juntos, porque é assim que a
                        divergência se explica sozinha depois (RN-168). */}
                    {linha !== undefined && ` · ${linha.reason} — ${linha.byName}`}
                  </span>
                </span>

                {aprovado !== undefined ? (
                  <span
                    className="flex items-center gap-1.5"
                    title={`Serviço ${textoDaAprovacao(aprovado)}: a quantidade não muda mais.`}
                  >
                    <span className="tabular">
                      {linha?.effect === 'REMOVE' ? 'não acontece aqui' : metros(daqui, 3)}
                    </span>
                    <Lock className="size-3.5 text-(--color-state-ok)" aria-hidden="true" />
                    <span className="font-medium text-(--color-state-ok)">aprovado</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    {linha?.effect === 'REMOVE' ? (
                      <Badge tone="warn">não acontece aqui</Badge>
                    ) : (
                      <NumeroOpcionalInput
                        aria-label={`Quantidade de ${item.description} em ${unit.floor.name}`}
                        className="h-9 w-24 text-right md:h-8"
                        /*
                        Travado sem motivo, como o botão ao lado já era: o número digitado
                        seria recusado pelo redutor e some no `onBlur`, e ver o valor
                        voltar sozinho não ensina nada a ninguém.
                      */
                        disabled={semMotivo}
                        value={daqui}
                        onChange={(quantity) => {
                          if (quantity === undefined) return;
                          dispatch({
                            type: 'SET_UNIT_ITEM',
                            unitKey: unit.key,
                            serviceKey: servico.key,
                            itemKey: item.key,
                            effect: 'REPLACE',
                            quantity,
                            reason: motivo,
                            by: sessao.userId,
                            byName: sessao.userName,
                          });
                        }}
                      />
                    )}
                    {linha !== undefined && <Badge tone="info">diverge</Badge>}

                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={semMotivo || linha?.effect === 'REMOVE'}
                      onClick={() =>
                        dispatch({
                          type: 'SET_UNIT_ITEM',
                          unitKey: unit.key,
                          serviceKey: servico.key,
                          itemKey: item.key,
                          effect: 'REMOVE',
                          reason: motivo,
                          by: sessao.userId,
                          byName: sessao.userName,
                        })
                      }
                    >
                      Não acontece
                    </Button>

                    {linha !== undefined && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Devolver ${item.description} à tipologia`}
                        onClick={() =>
                          dispatch({
                            type: 'CLEAR_UNIT_ITEM',
                            unitKey: unit.key,
                            serviceKey: servico.key,
                            itemKey: item.key,
                          })
                        }
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {semMotivo && (
        <p className="text-xs text-(--color-state-warn)">
          Escreva o motivo antes de mudar um número: é ele que explica a diferença para quem abrir
          isto depois (RN-168).
        </p>
      )}
    </div>
  );
}
